import React, { useMemo } from 'react';
import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Paper,
  IconButton,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Grid,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import { CancelButton } from 'components/CancelButton';
import { PrimaryButton } from 'components/PrimaryButton';
import { DialogCloseButton } from '../../../components/DialogCloseButton';
import { DialogOpenButton } from '../../../components/DialogOpenButton';
import { ErrorIndicator } from '../../../components/ErrorIndicator';
import { LoadingIndicator } from '../../../components/LoadingIndicator';
import { AutocompleteWrapper } from '../../../components/AutocompleteWrapper';
import { GridViewForm } from './GridViewForm';
import { useGridViewModalState } from 'components/hooks/UseGridViewModalState';
import { useTransitionEnumById } from 'components/hooks/UseTransitionEnums';
import { ComponentGridViewModel, GridViewState } from '../../../data/GridViewData';
import {
  selectedScopeOptions,
  COMPONENT_NAME as SELECTED_STEP_COMPONENT_NAME,
} from 'researchProjectManagement/sampleTracking/SampleTrackingSelectedSampleGrid';
import './GridView.css';
import { PersistentDialog } from '../../PersistentDialog';

interface GridViewModalProps {
  onClose?: () => void;
  onApplyView?: (viewModel: ComponentGridViewModel) => void;
  componentName: string;
  transitionEnumId?: string;
  configuredTransitionId?: string;
  researchProjectId?: string;
  getCurrentGridState: () => GridViewState;
  currentViewModel: ComponentGridViewModel;
}

export const GridViewModal = ({
  onClose,
  onApplyView,
  componentName,
  transitionEnumId,
  configuredTransitionId,
  researchProjectId,
  getCurrentGridState,
  currentViewModel,
}: GridViewModalProps) => {
  const {
    isOpen,
    filterState,
    modalState,
    gridLayouts,
    gridFilters,
    loadingState,
    canApplyView,
    dispatchFilter,
    dispatchModal,
    setIsOpen,
    closeModal,
    saveLayout,
    updateLayout,
    deleteLayout,
    saveFilter,
    updateFilter,
    deleteFilter,
    handleApplyView,
    layoutForm,
    filterForm,
  } = useGridViewModalState({
    componentName,
    transitionEnumId,
    configuredTransitionId,
    researchProjectId,
    onClose,
    onApplyView,
    getCurrentGridState,
    currentViewModel,
  });

  const transitionEnum = useTransitionEnumById(transitionEnumId);

  const formatTransitionName = (name: string | undefined) => {
    if (!name) return 'current';
    return name.replace(/([A-Z])/g, ' $1').trim();
  };

  const scopeOptions = useMemo(() => {
    return componentName === SELECTED_STEP_COMPONENT_NAME
      ? selectedScopeOptions
      : [
          { key: 'currentStep', label: 'Current step', visible: configuredTransitionId !== undefined },
          { key: 'researchProject', label: 'All steps in this project', visible: researchProjectId !== undefined },
          {
            key: 'stepType',
            label: `All steps of ${formatTransitionName(transitionEnum?.name)} type`,
            visible: transitionEnumId !== undefined,
          },
          { key: 'none', label: 'All steps in every project', visible: true },
        ];
  }, [componentName, configuredTransitionId, researchProjectId, transitionEnumId, transitionEnum]);

  const scopeLabel = useMemo(() => {
    return scopeOptions.find(option => option.key === filterState.scope)?.label;
  }, [filterState.scope, scopeOptions]);

  const isLoading = loadingState.status === 'Loading';
  const isFormDirty = layoutForm.formState.isDirty || filterForm.formState.isDirty;

  const handleAddLayout = () => {
    dispatchModal({ type: 'SELECT_LAYOUT', layout: null });
    dispatchModal({ type: 'TOGGLE_LAYOUT_FORM' });
  };

  const handleAddFilter = () => {
    dispatchModal({ type: 'SELECT_FILTER', filter: null });
    dispatchModal({ type: 'TOGGLE_FILTER_FORM' });
  };

  if (!componentName) return null;

  return (
    <>
      <DialogOpenButton onClick={() => setIsOpen(true)} sx={{ pr: 1, pl: 3, justifyContent: 'flex-start' }}>
        <VisibilityIcon />
      </DialogOpenButton>
      <PersistentDialog open={isOpen} onClose={closeModal} fullWidth maxWidth='sm'>
        <DialogTitle variant='h3'>
          <DialogCloseButton onClick={closeModal} />
          Grid View Settings
        </DialogTitle>
        <DialogContent dividers sx={{ background: '#fafbfc' }}>
          <Box component='div' sx={{ flexGrow: 1, mt: 1 }}>
            <Typography variant='h4' sx={{ mb: 2 }}>
              Scope Selection
            </Typography>
            <FormControl component='fieldset' sx={{ mb: 2, width: '100%' }}>
              <RadioGroup
                value={filterState.scope}
                onChange={e => dispatchFilter({ type: 'SET_SCOPE', scope: e.target.value as any })}
              >
                <Grid container spacing={0} sx={{ width: '100%', maxWidth: '100%' }}>
                  {scopeOptions
                    .filter(option => option.visible)
                    .map(option => (
                      <Grid item xs={6} key={option.key} sx={{ whiteSpace: 'nowrap' }}>
                        <FormControlLabel value={option.key} control={<Radio />} label={option.label} />
                      </Grid>
                    ))}
                </Grid>
              </RadioGroup>
            </FormControl>
            <Paper elevation={1} className='grid-view-paper'>
              <Box className='grid-view-box'>
                <Typography variant='h4'>Layout Settings</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    color='primary'
                    onClick={() => dispatchModal({ type: 'TOGGLE_LAYOUT_FORM' })}
                    disabled={modalState.selectedLayout === null || !modalState.selectedLayout.canWrite}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton color='primary' onClick={handleAddLayout}>
                    <AddCircleRoundedIcon />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ p: 2, pb: 0 }}>
                <AutocompleteWrapper
                  fullWidth
                  value={modalState.selectedLayout}
                  options={gridLayouts}
                  loading={isLoading}
                  getOptionLabel={option => (typeof option === 'string' ? option : option.name)}
                  isOptionEqualToValue={(option: any, value: any) =>
                    typeof option === 'string' ? option === value : option.name === value?.name
                  }
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Typography variant='body2' sx={{ fontWeight: 500 }}>
                          {option.name}
                        </Typography>
                        {option.owner && (
                          <Typography variant='caption' className='owner-text'>
                            Owner: {option.owner}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  renderInput={params => <TextField {...params} label='Layout' margin='dense' />}
                  onChange={(_, value) => dispatchModal({ type: 'SELECT_LAYOUT', layout: value })}
                />
              </Box>
              <Box sx={{ p: 2, pt: 0 }}>
                {(modalState.showLayoutForm || modalState.selectedLayout) && (
                  <GridViewForm
                    recordType='layout'
                    form={layoutForm}
                    selectedRecord={modalState.selectedLayout}
                    onSave={saveLayout}
                    onUpdate={updateLayout}
                    onCancel={() => dispatchModal({ type: 'TOGGLE_LAYOUT_FORM' })}
                    onDelete={modalState.selectedLayout?.canAdmin ? deleteLayout : undefined}
                    isLoading={isLoading}
                    showForm={modalState.showLayoutForm}
                    scopeLabel={scopeLabel}
                  />
                )}
              </Box>
            </Paper>
            <Paper elevation={1} className='grid-view-paper'>
              <Box className='grid-view-box'>
                <Typography variant='h4'>Filter Settings</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    color='primary'
                    onClick={() => dispatchModal({ type: 'TOGGLE_FILTER_FORM' })}
                    disabled={modalState.selectedFilter === null || !modalState.selectedFilter.canWrite}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton color='primary' onClick={handleAddFilter}>
                    <AddCircleRoundedIcon />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ p: 2, pb: 0 }}>
                <AutocompleteWrapper
                  fullWidth
                  value={modalState.selectedFilter}
                  options={gridFilters}
                  loading={isLoading}
                  getOptionLabel={option => (typeof option === 'string' ? option : option.name)}
                  isOptionEqualToValue={(option: any, value: any) =>
                    typeof option === 'string' ? option === value : option.name === value?.name
                  }
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Typography variant='body2' sx={{ fontWeight: 500 }}>
                          {option.name}
                        </Typography>
                        {option.owner && (
                          <Typography variant='caption' className='owner-text'>
                            Owner: {option.owner}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  renderInput={params => <TextField {...params} label='Filter' margin='dense' />}
                  onChange={(_, value) => dispatchModal({ type: 'SELECT_FILTER', filter: value })}
                />
              </Box>
              <Box sx={{ p: 2, pt: 0 }}>
                {(modalState.showFilterForm || modalState.selectedFilter) && (
                  <GridViewForm
                    recordType='filter'
                    form={filterForm}
                    selectedRecord={modalState.selectedFilter}
                    onSave={saveFilter}
                    onUpdate={updateFilter}
                    onCancel={() => dispatchModal({ type: 'TOGGLE_FILTER_FORM' })}
                    onDelete={modalState.selectedFilter?.canAdmin ? deleteFilter : undefined}
                    isLoading={isLoading}
                    showForm={modalState.showFilterForm}
                    scopeLabel={scopeLabel}
                  />
                )}
              </Box>
            </Paper>
          </Box>
        </DialogContent>
        <LoadingIndicator loadingState={loadingState} margin='LR' />
        <ErrorIndicator loadingState={loadingState} />
        <DialogActions>
          <CancelButton onClick={closeModal} disabled={isLoading} />
          <PrimaryButton onClick={handleApplyView} disabled={isLoading || !canApplyView || isFormDirty}>
            Apply View
          </PrimaryButton>
        </DialogActions>
      </PersistentDialog>
    </>
  );
};
